import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { ToastContext, type ToastOptions } from '../../contexts/ToastContext';
import { Toast, TOAST_DEFAULT_DURATION_MS } from './Toast';

interface QueuedToast {
    id: number;
    message: string;
    tone: ToastOptions['tone'];
    duration: number;
}

/** No more than this many toasts render at once — a fourth queues instead of stacking. */
const MAX_VISIBLE_TOASTS = 3;

export interface ToastProviderProps {
    children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
    const [visible, setVisible] = useState<QueuedToast[]>([]);
    const queueRef = useRef<QueuedToast[]>([]);
    const nextIdRef = useRef(0);

    const drain = useCallback(() => {
        setVisible((current) => {
            if (current.length >= MAX_VISIBLE_TOASTS) return current;
            const next = queueRef.current.shift();
            return next ? [...current, next] : current;
        });
    }, []);

    const dismiss = useCallback((id: number) => {
        setVisible((current) => current.filter((toast) => toast.id !== id));
        drain();
    }, [drain]);

    const show = useCallback((message: string, opts?: ToastOptions) => {
        const toast: QueuedToast = {
            id: nextIdRef.current++,
            message,
            tone: opts?.tone ?? 'neutral',
            duration: opts?.duration ?? TOAST_DEFAULT_DURATION_MS,
        };
        setVisible((current) => {
            if (current.length >= MAX_VISIBLE_TOASTS) {
                queueRef.current.push(toast);
                return current;
            }
            return [...current, toast];
        });
    }, []);

    const value = useMemo(() => ({ show }), [show]);

    return (
        <ToastContext.Provider value={value}>
            {children}
            <div
                className="pointer-events-none fixed left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2"
                style={{ top: 76 }}
            >
                {visible.map((toast) => (
                    <Toast
                        key={toast.id}
                        message={toast.message}
                        tone={toast.tone}
                        duration={toast.duration}
                        onDismiss={() => dismiss(toast.id)}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export default ToastProvider;
